import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Lock,
  User,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_API_URL}/api`;

export default function Register() {
  const { t, i18n } = useTranslation();

  const [step, setStep] = useState(1); // 1 = form, 2 = email verification
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    password_confirm: "",
    subscribe_product_updates: false,
  });
  const [showPass, setShowPass] = useState(false);
  const [showPassConfirm, setShowPassConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [resending, setResending] = useState(false);

  const handleGoogleLogin = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = encodeURIComponent(
      `${window.location.origin}/auth/callback`,
    );
    const scope = encodeURIComponent("openid email profile");
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&access_type=offline&prompt=select_account`;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      setError(t("register.passwordHint"));
      return;
    }
    if (form.password !== form.password_confirm) {
      setError(t("register.password_mismatch"));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const detectedLang = i18n.language?.split("-")[0] || "id";
      const res = await axios.post(`${API}/auth/register`, {
        name: form.name,
        email: form.email,
        password: form.password,
        agree_terms: true,
        subscribe_product_updates: form.subscribe_product_updates,
        language: detectedLang,
      });
      // Show email verification step
      setRegisteredEmail(res.data.email);
      setStep(2);
      toast.success(t("register.verify.emailSent"));
    } catch (err) {
      if (err.response?.status === 429) {
        setError(t("register.error.tooMany"));
      } else {
        setError(err.response?.data?.detail || t("register.error.registerFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    setError(null);
    try {
      await axios.post(`${API}/auth/resend-verification`, {
        email: registeredEmail,
      });
      toast.success(t("resend.success"));
    } catch (err) {
      if (err.response?.status === 429) {
        setError(t("register.error.tooMany"));
      } else {
        setError(err.response?.data?.detail || t("register.error.resendFailed"));
      }
    } finally {
      setResending(false);
    }
  };

  const maskEmail = (e) => {
    if (!e) return "";
    const [user, domain] = e.split("@");
    return `${user.slice(0, 2)}***@${domain}`;
  };

  const HERO_IMG =
    "https://images.unsplash.com/photo-1633356122544-f134324a6cee?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

  const FEATURES = [
    t("register.hero.f1"),
    t("register.hero.f2"),
    t("register.hero.f3"),
  ];

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* LEFT — Register Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h2 className="font-heading text-2xl font-bold tracking-tight">
                    {t("register.title")}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t("register.subtitle")}{" "}
                    <Link
                      to="/login"
                      className="text-brand-primary hover:underline font-medium"
                      data-testid="go-to-login"
                    >
                      {t("register.signinLink")}
                    </Link>
                  </p>
                </div>

                <Button
                  onClick={handleGoogleLogin}
                  variant="outline"
                  className="w-full rounded-md gap-3 font-medium"
                  data-testid="google-register-btn"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  {t("register.google")}
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-background px-2 text-muted-foreground font-mono">
                      {t("register.divider")}
                    </span>
                  </div>
                </div>

                <form onSubmit={handleRegister} className="space-y-4">
                  {error && (
                    <div
                      className="flex items-center gap-2 p-3 rounded-sm bg-red-500/10 border border-red-500/30"
                      data-testid="register-error"
                    >
                      <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <p className="text-sm text-red-400">{error}</p>
                    </div>
                  )}
                  {[
                    {
                      field: "name",
                      label: t("register.name"),
                      type: "text",
                      icon: User,
                      placeholder: "John Doe",
                      testid: "register-name",
                    },
                    {
                      field: "email",
                      label: t("register.email"),
                      type: "email",
                      icon: Mail,
                      placeholder: "you@example.com",
                      testid: "register-email",
                    },
                  ].map(
                    ({
                      field,
                      label,
                      type,
                      icon: Icon,
                      placeholder,
                      testid,
                    }) => (
                      <div key={field} className="space-y-1.5">
                        <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                          {label}
                        </label>
                        <div className="relative">
                          <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                          <Input
                            type={type}
                            value={form[field]}
                            onChange={(e) =>
                              setForm({ ...form, [field]: e.target.value })
                            }
                            placeholder={placeholder}
                            required
                            className="pl-9 rounded-sm text-sm"
                            data-testid={testid}
                          />
                        </div>
                      </div>
                    ),
                  )}
                  <div className="space-y-1.5">
                    <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                      {t("register.password")}
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <Input
                        type={showPass ? "text" : "password"}
                        value={form.password}
                        onChange={(e) =>
                          setForm({ ...form, password: e.target.value })
                        }
                        placeholder={t("register.passwordHint")}
                        required
                        minLength={8}
                        className="pl-9 pr-9 rounded-sm text-sm"
                        data-testid="register-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPass(!showPass)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPass ? (
                          <EyeOff className="w-3.5 h-3.5" />
                        ) : (
                          <Eye className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                      {t("register.password_confirm")}
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <Input
                        type={showPassConfirm ? "text" : "password"}
                        value={form.password_confirm}
                        onChange={(e) =>
                          setForm({ ...form, password_confirm: e.target.value })
                        }
                        placeholder={t("register.passwordHint")}
                        required
                        minLength={8}
                        className="pl-9 pr-9 rounded-sm text-sm"
                        data-testid="register-password-confirm"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassConfirm(!showPassConfirm)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassConfirm ? (
                          <EyeOff className="w-3.5 h-3.5" />
                        ) : (
                          <Eye className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-4 pt-2">
                    <div className="flex items-start gap-2.5">
                      <input
                        type="checkbox"
                        id="subscribe_product_updates"
                        checked={form.subscribe_product_updates}
                        onChange={(e) =>
                          setForm({ ...form, subscribe_product_updates: e.target.checked })
                        }
                        className="w-4 h-4 rounded border border-border bg-background cursor-pointer accent-brand-primary mt-0.5 flex-shrink-0"
                        data-testid="subscribe-product-updates-checkbox"
                      />
                      <label
                        htmlFor="subscribe_product_updates"
                        className="text-xs text-muted-foreground cursor-pointer leading-relaxed"
                      >
                        {t("register.product_updates")}
                      </label>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full rounded-md bg-brand-primary hover:bg-brand-primary/90 text-white font-medium"
                      data-testid="register-submit-btn"
                    >
                      {loading ? t("common.loading") : t("register.submit")}
                    </Button>

                    <div className="pt-4 border-t border-border/40">
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {t("register.terms_notice_prefix")}{" "}
                        <a
                          href="/terms"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand-primary hover:underline font-medium"
                        >
                          {t("register.terms")}
                        </a>{" "}
                        {t("register.and_privacy")}{" "}
                        <a
                          href="/privacy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand-primary hover:underline font-medium"
                        >
                          {t("register.privacy")}
                        </a>
                        . {t("register.terms_notice_suffix")}
                      </p>
                    </div>
                  </div>
                </form>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-6 text-center"
              >
                <div>
                  <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold tracking-tight">
                    {t("register.step2.title")}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-2">
                    {t("register.step2.subtitle")}
                  </p>
                  <p className="font-mono text-foreground font-medium mt-1">
                    {maskEmail(registeredEmail)}
                  </p>
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-sm bg-red-500/10 border border-red-500/30">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <p className="text-sm text-red-400">{error}</p>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2 text-left">
                  <h3 className="text-sm font-semibold text-blue-900">{t("register.step2.nextStepsTitle")}</h3>
                  <ol className="text-xs text-blue-700 space-y-1.5 list-decimal list-inside">
                    <li>{t("register.step2.next1")}</li>
                    <li>{t("register.step2.next2")}</li>
                    <li>{t("register.step2.next3")}</li>
                  </ol>
                  <p className="text-xs text-blue-600 pt-2 border-t border-blue-200">
                    {t("register.step2.notePrefix")} <strong>{t("register.step2.noteDuration")}</strong>
                  </p>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={handleResendVerification}
                    disabled={resending}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-border text-foreground hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    {resending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    {resending ? t("resend.submitting") : t("register.step2.resendPrompt")}
                  </button>

                  <button
                    onClick={() => {
                      setStep(1);
                      setError(null);
                      setForm({ ...form, password: "", password_confirm: "" });
                    }}
                    className="w-full text-sm text-muted-foreground hover:text-foreground font-medium transition"
                  >
                    {t("register.step2.backToForm")}
                  </button>
                </div>

                <div className="pt-4 border-t border-border text-xs text-muted-foreground">
                  <p>
                    {t("register.step2.haveAccount")}{" "}
                    <Link
                      to="/login"
                      className="text-brand-primary hover:underline font-medium"
                    >
                      {t("register.signinLink")}
                    </Link>
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* RIGHT — Hero Info + Image */}
      <div
        className="hidden lg:flex w-[50%] relative overflow-hidden border-l border-border"
        data-testid="register-hero-panel"
      >
        <img
          src={HERO_IMG}
          alt="Veriksa Hero"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/70 to-brand-primary/30" />
        <div className="relative z-10 flex flex-col justify-center p-14 text-foreground">
          <div className="mb-8">
            <span className="text-xs font-mono uppercase tracking-widest text-brand-primary">
              {t("register.hero.badge")}
            </span>
            <h2
              className="font-heading text-4xl font-bold tracking-tight mt-3 leading-tight"
              style={{ whiteSpace: "pre-line" }}
            >
              {t("register.hero.title")}
            </h2>
            <p className="text-muted-foreground mt-4 text-sm leading-relaxed max-w-sm">
              {t("register.hero.desc")}
            </p>
          </div>
          <div className="space-y-3">
            {FEATURES.map((f) => (
              <div key={f} className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-brand-primary mt-0.5 flex-shrink-0" />
                <span className="text-sm text-muted-foreground">{f}</span>
              </div>
            ))}
          </div>
          <div className="mt-10 pt-8 border-t border-border/40">
            <p className="text-xs font-mono text-muted-foreground/60">
              {t("register.hero.footer")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
